import { CalculatorFormValues } from 'common/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components/macro';
import { Field } from 'react-final-form';
import * as validators from 'validators';
import { FieldError, RadioButton, StepTitle } from '@net0-calc/common/components/ui';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { CheckIcon, ClockIcon } from '@net0-calc/common/assets/svg';
import { RedAccentHeading } from 'components';
import { WizardPageProps } from '@net0-calc/common/types';

const LABEL_CSS = css`
  ${TYPOGRAPHY.bodyRegular16}
`;

const WelcomePage: React.FC<WizardPageProps<CalculatorFormValues>> = () => {
  const { t } = useTranslation();

  const options = [
    {
      value: 'attendees',
      label: t('events.pages.welcome.form.field.welcome.options.attendees'),
    },
    {
      value: 'catering',
      label: t('events.pages.welcome.form.field.welcome.options.catering'),
    },
    {
      value: 'venueSize',
      label: t('events.pages.welcome.form.field.welcome.options.venueSize'),
    },
  ];

  return (
    <Root>
      <AnimatedItem $delay={0}>
        <RedAccentHeading>
          <StepTitle dangerouslySetInnerHTML={{ __html: t('events.pages.welcome.title') }}></StepTitle>
        </RedAccentHeading>
      </AnimatedItem>
      <AnimatedItem $delay={1}>
        <div>
          <Description>{t('events.pages.welcome.eventInfo')}</Description>
          <Description dangerouslySetInnerHTML={{ __html: t('events.pages.welcome.description') }}></Description>
        </div>
      </AnimatedItem>
      <AnimatedItem $delay={2}>
        <EstimationSection>
          <StyledClockIcon />
          <Text>{t('events.pages.welcome.estimation')}</Text>
        </EstimationSection>
      </AnimatedItem>
      <AnimatedItem $delay={3}>
        <div>
          {options.map(({ label, value }) => (
            <Field key={value} name="welcome" type="checkbox" value={value} validate={validators.isArrayMinLength(3)}>
              {({ input }) => <RadioButton text={label} textCSS={LABEL_CSS} icon={CheckIcon} {...input} />}
            </Field>
          ))}
          <FieldError name="welcome" />
        </div>
      </AnimatedItem>
    </Root>
  );
};

export default WelcomePage;

const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 20px;

  @media ${DEVICE.laptop} {
    margin-bottom: 70px;
    height: auto;
    flex-grow: 1;
    justify-content: flex-start;
  }
`;

const AnimatedItem = styled.div<{ $delay: number }>`
  opacity: 0;
  animation: ${fadeSlideUp} 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => 0.1 + $delay * 0.15}s;
`;

const Text = styled.div`
  ${TYPOGRAPHY.bodyRegular16}
  color: var(--color6);
`;

const Description = styled(Text)`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 16px;
  }
`;

const EstimationSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  @media ${DEVICE.laptop} {
    margin-bottom: 40px;
  }
`;

const StyledClockIcon = styled(ClockIcon)`
  margin-right: 10px;
  stroke: var(--color2);
`;
